/**
 * Разбор адреса в узел дерева (Спека 2a, GOCANVAS-638).
 *
 * Отдельные роуты `/board/` и `/page/` вместо единого `/node/<id>`: SPA —
 * статический бандл и вид узла заранее не знает, поэтому единый роут стоил бы
 * round-trip'а до монтирования. `kind` в модели неизменяем, так что ссылка не
 * может «протухнуть по типу».
 *
 * Оба id-формата (ULID-26 и hex-32) принимаются для обоих роутов — как это
 * было для борда до 2a. Legacy `?board=<id>` сохраняется.
 *
 * Порядок источников — path → query → default. FOR-196: `POST /v1/boards`
 * (`handlers_boards.go`) возвращает hex-32 форму id, и страница Workspaces
 * кладёт её прямо в `/board/<hex>`. Раньше путевая регулярка знала только
 * ULID, поэтому не совпадала с hex-адресом, функция падала на fallback, и
 * КАЖДЫЙ клик «открыть workspace» грузил демо-борд вместо запрошенного —
 * это была реальная регрессия, а не гипотетический риск. Отсюда и обе формы
 * в PATH_RE ниже, не только историческая параллель с бордом.
 *
 * Срезание деплой-базы из pathname перед матчингом: SPA может быть развёрнута
 * под не-корневой BASE_URL (e.g. `npm run build:e2e` печёт `/e2e/` по умолчанию).
 * Без срезания адрес `/e2e/board/<id>` не совпадёт с регуляркой `^/?board/...`
 * и функция упадёт на fallback, приводя к тому что SPA свалится на демо-борд
 * вместо того чтобы открыть запрошенную доску. Это регрессия, которая была
 * реальной для путей e2e-сборки.
 */
export type NodeKind = 'board' | 'page';

export type NodeRoute =
  | { id: string; kind: 'board' }
  | { id: string; kind: 'page'; fromBoardId: string | undefined };

const ULID = '[0-9A-HJKMNP-TV-Z]{26}';
const HEX = '[0-9a-f]{32}';
const PATH_RE = new RegExp(`^/?(board|page)/(${ULID}|${HEX})/?$`, 'i');
// Same id class as the path branch, anchored — legacy `?board=<id>` must
// not accept anything PATH_RE would reject. The value flows into URL
// concatenation and localStorage keys downstream; this is not an
// exploitable hole (no origin switch, no XSS), but leaving it unchecked
// left the invariant the path branch enforces untested for this branch.
const ID_RE = new RegExp(`^(${ULID}|${HEX})$`, 'i');

/** Parse only canonical SPA paths. Unlike readNodeRoute this deliberately
 * rejects the legacy query fallback, so unrelated links stay native. */
export function parseNodeRouteUrl(url: URL, basePath: string): NodeRoute | null {
  const normalizedBase = `/${basePath}`.replace(/\/{2,}/gu, '/').replace(/\/?$/u, '/');
  if (!url.pathname.startsWith(normalizedBase)) return null;
  const routePath = normalizedBase === '/'
    ? url.pathname
    : `/${url.pathname.slice(normalizedBase.length)}`;
  if (!PATH_RE.test(routePath)) return null;
  return readNodeRoute({ pathname: url.pathname, search: url.search }, '', normalizedBase);
}

export function readNodeRoute(
  loc: { pathname: string; search: string } = window.location,
  fallbackId = '',
  base?: string,
): NodeRoute {
  // Срежем префикс BASE_URL из pathname перед матчингом.
  // Дефолт читаем из import.meta.env.BASE_URL, но параметр позволяет
  // тестам подставить другое значение (e.g. '/e2e/' в тесте, '/' в прове).
  const resolvedBase = (base ?? (import.meta.env?.BASE_URL ?? '/')).replace(/\/+$/, '');
  let path = loc.pathname;
  if (resolvedBase && path.startsWith(resolvedBase)) {
    path = path.slice(resolvedBase.length);
  }

  const m = PATH_RE.exec(path);
  if (m) {
    const kind = m[1]!.toLowerCase() as NodeKind;
    if (kind === 'page') {
      const from = new URLSearchParams(loc.search).get('from');
      return {
        id: m[2] as string,
        kind,
        fromBoardId: from !== null && ID_RE.test(from) ? from : undefined,
      };
    }
    return { id: m[2] as string, kind };
  }
  const q = new URLSearchParams(loc.search).get('board');
  if (q && ID_RE.test(q)) return { id: q, kind: 'board' };
  return { id: fallbackId, kind: 'board' };
}
